import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { NAV_CATEGORIES as STATIC } from '@/data/navCategories';
import { categoriesApi } from '@/services/api';

const NavCategoriesContext = createContext(null);

function normalizeSub(sub) {
  if (!sub || typeof sub !== 'object') return { name: String(sub), slug: String(sub), image: '' };
  return {
    name: sub.name,
    slug: sub.slug,
    image: sub.image || sub.image_url || '',
  };
}

/**
 * Standardunderkategorier (navCategories.js) fanns ofta inte i MongoDB — de syntes bara via fallback.
 * Så fort admin skapade *en* underkategori returnerade API en lista med bara den raden, och vi bytte då ut
 * hela menyn mot API-listan → alla statiska underkategorier "försvann". Lösning: union per slug.
 */
function mergeSubcategoryLists(staticSubs, apiSubs) {
  const staticN = (staticSubs || []).map(normalizeSub);
  const apiN = (apiSubs || []).map(normalizeSub);
  const apiBySlug = new Map(apiN.map((s) => [s.slug, s]));
  const staticSlugs = new Set(staticN.map((s) => s.slug));

  const out = staticN.map((s) => {
    const a = apiBySlug.get(s.slug);
    if (!a) return s;
    return {
      name: a.name || s.name,
      slug: a.slug || s.slug,
      image: a.image || s.image || '',
    };
  });

  for (const a of apiN) {
    if (!staticSlugs.has(a.slug)) {
      out.push(a);
    }
  }
  return out;
}

function mergeNavTree(apiCats) {
  if (!apiCats?.length) return STATIC;
  return apiCats.map((c) => {
    const staticMatch = STATIC.find((s) => s.id === c.id);
    const fromApi = c.subcategories || [];
    const staticSubs = staticMatch?.subcategories || [];
    const subs = mergeSubcategoryLists(staticSubs, fromApi);
    return {
      id: c.id,
      name: c.name,
      slug: c.slug,
      image: c.image || c.image_url || staticMatch?.image || '',
      subcategories: subs,
    };
  });
}

async function loadMergedNavTree() {
  try {
    const { data } = await categoriesApi.getNavTree();
    return mergeNavTree(data?.categories);
  } catch {
    return STATIC;
  }
}

function staticHelpers() {
  const getSubcategoriesForParent = (parentId) => {
    const cat = STATIC.find((c) => c.id === parentId);
    return (cat?.subcategories || []).map(normalizeSub);
  };
  const findSubcategoryLabel = (parentId, subSlug) => {
    if (!subSlug) return '';
    const sub = getSubcategoriesForParent(parentId).find((x) => x.slug === subSlug);
    return sub?.name ?? subSlug;
  };
  return { getSubcategoriesForParent, findSubcategoryLabel };
}

export function NavCategoriesProvider({ children }) {
  const [categories, setCategories] = useState(STATIC);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      const merged = await loadMergedNavTree();
      if (!cancel) {
        setCategories(merged);
        setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const refreshNavCategories = useCallback(async () => {
    const merged = await loadMergedNavTree();
    setCategories(merged);
  }, []);

  const getSubcategoriesForParent = useCallback(
    (parentId) => {
      const cat = categories.find((c) => c.id === parentId);
      return cat?.subcategories ?? [];
    },
    [categories],
  );

  const findSubcategoryLabel = useCallback(
    (parentId, subSlug) => {
      if (!subSlug) return '';
      const subs = categories.find((c) => c.id === parentId)?.subcategories ?? [];
      const sub = subs.find((x) => x.slug === subSlug);
      return sub?.name ?? subSlug;
    },
    [categories],
  );

  const value = useMemo(
    () => ({
      categories,
      loading,
      getSubcategoriesForParent,
      findSubcategoryLabel,
      refreshNavCategories,
    }),
    [
      categories,
      loading,
      getSubcategoriesForParent,
      findSubcategoryLabel,
      refreshNavCategories,
    ],
  );

  return (
    <NavCategoriesContext.Provider value={value}>{children}</NavCategoriesContext.Provider>
  );
}

export function useNavCategories() {
  const ctx = useContext(NavCategoriesContext);
  if (ctx) return ctx;
  const { getSubcategoriesForParent, findSubcategoryLabel } = staticHelpers();
  return {
    categories: STATIC,
    loading: false,
    getSubcategoriesForParent,
    findSubcategoryLabel,
    refreshNavCategories: async () => {},
  };
}
